import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { DelabNgxPageTitleComponent } from '@delab/ngx-ixconnect-components';
import { HeaderDto } from '@shared/dto/auth/Header.dto';
import { map, Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-info',
    templateUrl: './info.component.html',
    styleUrls: ['./info.component.scss'],
    imports: [
        // Angular
        CommonModule,
        // Angular Material
        MatButtonModule,
        // Own
        DelabNgxPageTitleComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InfoComponent implements OnInit, OnDestroy {
    private readonly _ngUnsubscribe = new Subject<void>();

    public readonly RELEASE_NOTES_URL = `https://update.ixconnect.com/electron-updateserver/ws/releasenotes/Einsenderportal/`;

    private readonly EMAIL_ADDRESSEE: string = 'support-labor@dedalus.com';
    private readonly EMAIL_SUBJECT: string = 'Einsenderportal: Supportanfrage';

    protected readonly APP_NAME = 'ix.lab | Einsenderportal';

    private readonly xccExtId = signal<string | undefined>(undefined);

    protected readonly infos = computed<{ label: string; value: string | undefined }[]>(() => this._constructInfos(this.xccExtId()));
    protected readonly mailtoLink = computed<string>(() => this._constructMailToLink(this.xccExtId()));

    constructor(private readonly _authService: AuthService) {}

    ngOnInit(): void {
        // TODO: Aktuell enthält der Mailto-Link vorerst weniger Infos, ist aber schon klickbar.
        // Hier sollte eine Möglichkeit geschaffen werden, dass er initial disabled ist und enabled wird, sobald die Infos da sind.
        // Sind die Infos nach einer bestimmten Zeit (z.B. 20 Sek.) noch nicht da, sollte der Button trotzdem enabled werden. Die Email enthält dann nur die vorhandenen Infos.
        // Falls die Infos später eintreffen, soll der Link aktualisiert werden.

        this._authService.headerObs
            .pipe(
                map((headers) => this._getHeaderValue(headers || [], 'x-xcc-extid')),
                takeUntil(this._ngUnsubscribe),
            )
            .subscribe({
                next: (xccExtId) => {
                    this.xccExtId.set(xccExtId);
                },
                error: (error) => {
                    console.error(`Could not load xccExtId from header data`, error);
                },
            });
    }

    ngOnDestroy() {
        this._ngUnsubscribe.next();
        this._ngUnsubscribe.complete();
    }

    private _constructInfos(xccExtId: string | undefined) {
        return [
            { label: 'Version', value: environment.version },
            { label: 'Benutzer', value: xccExtId },
        ];
    }

    private _constructMailToLink(xccExtId: string | undefined): string {
        const infos = [
            { name: 'App-Name', value: this.APP_NAME },
            { name: 'App-Version', value: environment.version },
            { name: 'Praxis', value: xccExtId },
        ];

        const infoString = infos.map(({ name, value }) => `${name}: ${value || '---'}`).join('\n');

        const body = `\n\n\n\n\n[Bitte beschreiben Sie oberhalb dieser Linie Ihr Problem und fügen Sie ggf. Screenshots hinzu.]\n-----------------------------------------------------------------------\n\nSysteminformationen:\n\n${infoString}\n`;
        const query = [
            { key: 'body', value: body },
            { key: 'subject', value: this.EMAIL_SUBJECT },
        ];

        const queryString = query.map(({ key, value }) => `${key}=${encodeURIComponent(value)}`).join('&');

        return `mailto:${this.EMAIL_ADDRESSEE}?${queryString}`;
    }

    private _getHeaderValue(headers: HeaderDto[], key: string): string | undefined {
        return headers.find((header) => header.key === key)?.value ?? undefined;
    }
}
